/**
 * WR% — Williams %R fast/slow EMA pair (Round 142, zone-logic update R116b).
 *
 * Two parameterised Williams %R lines, each smoothed by its own EMA, plotted
 * together. Williams %R output is bounded to [-100, 0]; smoothed EMAs of it
 * are bounded in the same range, so the pane is anchored to that fixed
 * scale and crossover visuals stay locked across zoom / scroll.
 *
 * Zone classification (R116b):
 *   BUY     — slowEMA[i] > -50  AND  fastEMA[i] > fastEMA[i-1]   (slow above
 *             the mid-line + fast EMA slope is positive)
 *   SHORT   — slowEMA[i] < -50  AND  fastEMA[i] < fastEMA[i-1]
 *   NEUTRAL — everything else (slow below mid + fast rising; slow above mid +
 *             fast falling; one side flat; or warm-up bars)
 *
 * Voter logic for OverView / Bkground / RockBand / scan / backtest:
 *   BUY    → 'up'   vote, weighted by the user's wrPct weight
 *   SHORT  → 'down' vote, same weight
 *   NEUTRAL→ 'na'   — WR% ABSTAINS, contributes nothing to the score
 *
 * The math is mirrored byte-for-byte in
 * backend/src/services/strategies/chartPort/wrPct.ts so scan and chart agree.
 */

export interface WrPctParams {
  source: 'close' | 'linregClose';
  fastLength: number;     // Williams %R lookback for the fast line
  fastEma: number;        // EMA period applied to the fast %R series
  slowLength: number;
  slowEma: number;
}

export const DEFAULT_WRPCT: WrPctParams = {
  // R116b — default source is LinReg(close, 14) per user preference: the
  // smoothed input gives cleaner zone transitions than raw close.
  source: 'linregClose',
  fastLength: 21,
  fastEma: 13,
  slowLength: 200,
  slowEma: 13,
};

/** Slow-EMA threshold that decides BUY-eligible (above) vs SHORT-eligible
 *  (below). Mid-point of Williams %R range. */
export const WRPCT_MID = -50;

/** Standard OB/OS levels for Williams %R. NOT user-configurable — they're a
 *  visual reference like JHM's 50-mid line and MACD's zero line. */
export const WRPCT_OVERBOUGHT = -20;
export const WRPCT_OVERSOLD   = -80;

export type WrPctZone = 'BUY' | 'SHORT' | 'NEUTRAL';

export function resolveWrPct(p?: Partial<WrPctParams>): WrPctParams {
  // R116b — fall back to LinReg if not stored, but honour an explicit 'close'.
  const src: 'close' | 'linregClose' =
    p?.source === 'close' ? 'close' : 'linregClose';
  return {
    source: src,
    fastLength: Math.max(1, Math.floor(Number(p?.fastLength) || DEFAULT_WRPCT.fastLength)),
    fastEma:    Math.max(1, Math.floor(Number(p?.fastEma)    || DEFAULT_WRPCT.fastEma)),
    slowLength: Math.max(1, Math.floor(Number(p?.slowLength) || DEFAULT_WRPCT.slowLength)),
    slowEma:    Math.max(1, Math.floor(Number(p?.slowEma)    || DEFAULT_WRPCT.slowEma)),
  };
}

/**
 * Classify the WR% zone at bar `i`. Single-bar slope on the fast EMA so a
 * flat fast EMA produces NEUTRAL. Returns 'NEUTRAL' during warm-up (when
 * fast[i], fast[i-1], or slow[i] are NaN) and when neither condition is met.
 */
export function wrPctZoneAt(
  fastArr: number[],
  slowArr: number[],
  i: number,
): WrPctZone {
  if (i < 1) return 'NEUTRAL';
  const f = fastArr[i], fPrev = fastArr[i - 1], s = slowArr[i];
  if (!Number.isFinite(f) || !Number.isFinite(fPrev) || !Number.isFinite(s)) {
    return 'NEUTRAL';
  }
  if (s > WRPCT_MID && f > fPrev) return 'BUY';
  if (s < WRPCT_MID && f < fPrev) return 'SHORT';
  return 'NEUTRAL';
}

/**
 * Raw Williams %R: 100 × (close − highest(N)) / (highest(N) − lowest(N)).
 * Range [-100, 0]. NaN until `length` closed bars are available. Highs and
 * lows are walked in O(N×length); fine at length ≤ 200 + a few hundred bars.
 */
export function williamsPercentR(
  closes: number[],
  highs: number[],
  lows: number[],
  length: number,
): number[] {
  const n = closes.length;
  const out = new Array<number>(n).fill(NaN);
  if (n < length || length < 1) return out;
  for (let i = length - 1; i < n; i++) {
    let hi = -Infinity, lo = Infinity, ok = true;
    for (let j = i - length + 1; j <= i; j++) {
      const h = highs[j], l = lows[j];
      if (!Number.isFinite(h) || !Number.isFinite(l)) { ok = false; break; }
      if (h > hi) hi = h;
      if (l < lo) lo = l;
    }
    const c = closes[i];
    if (!ok || !Number.isFinite(c) || hi === lo) continue;
    out[i] = 100 * (c - hi) / (hi - lo);
  }
  return out;
}

/** SMA-seeded EMA, NaN-aware. Mirrors utils/jhm.emaSeries. */
function emaNaNAware(values: number[], period: number): number[] {
  const n = values.length;
  const out = new Array<number>(n).fill(NaN);
  if (period < 1) return out;
  const k = 2 / (period + 1);
  let prev = NaN, seeded = false, sum = 0, cnt = 0;
  for (let i = 0; i < n; i++) {
    const v = values[i];
    if (!Number.isFinite(v)) continue;
    if (!seeded) {
      sum += v; cnt++;
      if (cnt === period) { prev = sum / period; out[i] = prev; seeded = true; }
      continue;
    }
    prev = v * k + prev * (1 - k);
    out[i] = prev;
  }
  return out;
}

/**
 * Per-bar fast/slow EMA-of-Williams-%R pair.
 *  - `srcArr` is the close-or-linregClose series chosen by the user; highs
 *    and lows are always raw (because Williams %R defines high/low explicitly
 *    as bar extremes, regardless of close source).
 *  - Both EMA outputs are NaN until enough closed bars have accumulated
 *    (length + ema warmup).
 */
export function wrPctPair(
  srcArr: number[],
  highs: number[],
  lows: number[],
  p: WrPctParams,
): { fast: number[]; slow: number[] } {
  const fastR = williamsPercentR(srcArr, highs, lows, p.fastLength);
  const slowR = williamsPercentR(srcArr, highs, lows, p.slowLength);
  return {
    fast: emaNaNAware(fastR, p.fastEma),
    slow: emaNaNAware(slowR, p.slowEma),
  };
}
